/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, extendObservable } from 'mobx';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ToastStore } from 'src/luis/core/stores/ToastStore';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { ToastStoreHouse } from 'src/luis/types/stores/events/ToastStoreHouse';
import { SubtractStore } from 'src/luis/types/SubtractStore';

/**
 * A factory function for creating a toast store house,
 * which houses an array of toast stores.
 */
export const createToastStoreHouse = (): ToastStoreHouse => {
    const resourcesStoreHouse = createResourceStoreHouse<ToastStore>();

    const toastStoreHouse: SubtractStore<ToastStoreHouse, ResourceStoreHouse<ToastStore>> = {
        /**
         * Returns the number of unseen toasts.
         */
        get unseenToastsCount() {
            return this.items.filter(item => !item.seen).length;
        },

        /**
         * Marks a toast as dismissed
         *
         * @param id The id of the toast.
         */
        dismissById(id: string) {
            const toastStore = this.findById(id);
            toastStore.markAsDismissed();
        },

        /**
         * Marks all the toasts as dismissed.
         */
        dismissAll() {
            this.items.forEach(item => item.markAsDismissed());
        },

        /**
         * Marks all the toasts as seen.
         */
        markAllSeen() {
            this.items.forEach(item => item.markAsSeen());
        }
    };

    return extendObservable(
        resourcesStoreHouse,
        toastStoreHouse,
        {
            dismissById: action,
            dismissAll: action,
            markAllSeen: action
        },
        { deep: false }
    );
};
